import { useState, useEffect, useCallback, useRef } from 'react';
import DyteClient from '@cloudflare/realtimekit';

interface UseRealtimeKitOptions {
  authToken: string | null;
  onMeetingJoined?: () => void;
  onMeetingLeft?: () => void;
  onParticipantJoined?: (participant: any) => void;
  onParticipantLeft?: (participant: any) => void;
  onScreenShareUpdate?: (participantId: string, enabled: boolean) => void;
  onError?: (error: Error) => void;
}

interface UseRealtimeKitReturn {
  meeting: any;
  isInitialized: boolean;
  isJoined: boolean;
  error: string | null;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  isScreenSharing: boolean;
  participants: any[];
  self: any;
  toggleAudio: () => Promise<void>;
  toggleVideo: () => Promise<void>;
  toggleScreenShare: () => Promise<void>;
  leaveMeeting: () => Promise<void>;
  audioLevels: Map<string, number>;
  connectionQuality: Map<string, 'excellent' | 'good' | 'poor'>;
  screenShareTracks: Map<string, MediaStreamTrack>;
  getScreenShareTrack: (participantId: string) => MediaStreamTrack | null;
}

export function useRealtimeKit(options: UseRealtimeKitOptions): UseRealtimeKitReturn {
  const {
    authToken,
    onMeetingJoined,
    onMeetingLeft,
    onParticipantJoined,
    onParticipantLeft,
    onScreenShareUpdate,
    onError,
  } = options;

  const [meeting, setMeeting] = useState<any>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [self, setSelf] = useState<any>(null);
  const [audioLevels, setAudioLevels] = useState<Map<string, number>>(new Map());
  const [connectionQuality, setConnectionQuality] = useState<Map<string, 'excellent' | 'good' | 'poor'>>(new Map());
  const [screenShareTracks, setScreenShareTracks] = useState<Map<string, MediaStreamTrack>>(new Map());

  const meetingRef = useRef<any>(null);
  const isInitializingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  const initializeAndJoin = useCallback(async () => {
    if (!authToken) {
      setError('Missing auth token');
      return;
    }

    if (isInitializingRef.current || hasInitializedRef.current) {
      console.log('Already initializing or initialized');
      return;
    }

    isInitializingRef.current = true;

    try {
      setError(null);

      console.log('Initializing RealtimeKit...');
      const client = await DyteClient.init({
        authToken,
        defaults: {
          audio: false,
          video: false,
        },
      });

      meetingRef.current = client;
      setMeeting(client);
      setSelf(client.self);
      setIsInitialized(true);
      hasInitializedRef.current = true;

      setupEventListeners(client);

      await client.join();
      setIsJoined(true);
      onMeetingJoined?.();

      console.log('RealtimeKit initialized and joined');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to initialize meeting';
      setError(errorMsg);
      onError?.(err instanceof Error ? err : new Error(errorMsg));
      console.error('RealtimeKit initialization error:', err);
      
      hasInitializedRef.current = false;
    } finally {
      isInitializingRef.current = false;
    }
  }, [authToken, onMeetingJoined, onError]);

  const setupEventListeners = useCallback((client: any) => {
    if (!client) return;

    console.log('🔍 Setting up RealtimeKit listeners');

    // Network quality updates
    if (client.participants?.joined) {
      client.participants.joined.addListener('networkQualityUpdate', (data: any) => {
        setConnectionQuality(prev => {
          const next = new Map(prev);
          next.set(data.participantId, data.quality);
          return next;
        });
      });

      // Audio level and track updates - CONSOLIDATED INTO ONE LISTENER
      client.participants.joined.addListener('audioUpdate', (participant: any) => {
        console.log('🔊 Audio update for participant:', {
          id: participant.id,
          name: participant.name,
          audioEnabled: participant.audioEnabled,
          hasAudioTrack: !!participant.audioTrack,
          audioLevel: participant.audioLevel,
              audioTrackId: participant.audioTrack?.id,
    audioTrackReadyState: participant.audioTrack?.readyState,
        });
        
        // Update audio levels
        setAudioLevels(prev => {
          const next = new Map(prev);
          next.set(participant.id, participant.audioLevel || 0);
          return next;
        });

        // Force re-render by updating participants to trigger audio component updates
        setParticipants(prev => {
          const index = prev.findIndex(p => p.id === participant.id);
          if (index >= 0) {
            const updated = [...prev];
            updated[index] = participant;
            return updated;
          }
          return prev;
        });
      });
// client.participants.joined.addListener('audioUpdate', (participant: any) => {
//   console.log('🔊 Audio update for participant:', {
//     id: participant.id,
//     name: participant.name,
//     audioEnabled: participant.audioEnabled,
//     hasAudioTrack: !!participant.audioTrack,
//     audioLevel: participant.audioLevel,
//     audioTrackId: participant.audioTrack?.id,
//     audioTrackReadyState: participant.audioTrack?.readyState,
//   });
  
//   // CRITICAL: Store audio levels using RTK participant ID (participant.id)
//   setAudioLevels(prev => {
//     const next = new Map(prev);
//     next.set(participant.id, participant.audioLevel || 0);
//     return next;
//   });

//   // Force re-render by updating participants to trigger audio component updates
//   setParticipants(prev => {
//     const index = prev.findIndex(p => p.id === participant.id);
//     if (index >= 0) {
//       const updated = [...prev];
//       updated[index] = participant;
//       return updated;
//     }
//     return prev;
//   });
// });

// // ALSO ADD: Listen for self audio updates
// if (client.self?.on) {
//   client.self.on('audioUpdate', (data: any) => {
//     console.log('🎤 Self audio update:', {
//       audioEnabled: data.audioEnabled,
//       audioLevel: client.self.audioLevel,
//       selfId: client.self.id,
//     });
    
//     setIsAudioEnabled(data.audioEnabled);
    
//     // Update self audio level using self ID
//     setAudioLevels(prev => {
//       const next = new Map(prev);
//       next.set(client.self.id, client.self.audioLevel || 0);
//       return next;
//     });
//   });
// }

      // Screen share update events
      const tryEventNames = ['screenShareStarted', 'screenShareUpdate', 'screenShareEnabled'];
      tryEventNames.forEach(eventName => {
        client.participants.joined.addListener(eventName, (participant: any) => {
          console.log(`🎬 Event: ${eventName}`, participant.id);
          
          const track = 
            participant.screenShareTrack || 
            participant.screenShareTracks?.video ||
            participant.tracks?.screenShare;
          
          if (track) {
            console.log('✅ Found screen share track:', track.id);
            setScreenShareTracks(prev => {
              const next = new Map(prev);
              next.set(participant.id, track);
              return next;
            });
          } else {
            console.warn('⚠️ No screen share track found for:', participant.id);
          }
        });
      });

      // Screen share stopped
      const tryStopEvents = ['screenShareStopped', 'screenShareUpdate', 'screenShareDisabled'];
      tryStopEvents.forEach(eventName => {
        client.participants.joined.addListener(eventName, (participant: any) => {
          if (!participant.screenShareEnabled) {
            console.log(`🛑 Stop event: ${eventName}`, participant.id);
            setScreenShareTracks(prev => {
              const next = new Map(prev);
              next.delete(participant.id);
              return next;
            });
          }
        });
      });
    }

    // Participant joined
    if (client.participants?.joined?.on) {
      client.participants.joined.on('participantJoined', (participant: any) => {
        console.log('✅ Participant joined:', participant.name, participant.id);
        
        setParticipants(prev => {
          if (prev.some(p => p.id === participant.id)) return prev;
          return [...prev, participant];
        });
        
        // Check ALL existing participants for screen shares after a delay
        setTimeout(() => {
          if (client.participants?.joined) {
            Array.from(client.participants.joined.values()).forEach((p: any) => {
              if (p.screenShareEnabled) {
                const track = 
                  p.screenShareTrack || 
                  p.screenShareTracks?.video ||
                  p.tracks?.screenShare;
                
                if (track) {
                  console.log('🔍 Found existing screen share from:', p.id);
                  setScreenShareTracks(prev => {
                    const next = new Map(prev);
                    next.set(p.id, track);
                    return next;
                  });
                }
              }
            });
          }
        }, 1000);
        
        onParticipantJoined?.(participant);
      });
    }

    // Participant left
    if (client.participants?.left?.on) {
      client.participants.left.on('participantLeft', (participant: any) => {
        console.log('👋 Participant left:', participant.name);
        setParticipants(prev => prev.filter(p => p.id !== participant.id));
        
        setScreenShareTracks(prev => {
          const next = new Map(prev);
          next.delete(participant.id);
          return next;
        });
        
        onParticipantLeft?.(participant);
      });
    }

    // Self audio/video updates
    if (client.self?.on) {
      client.self.on('audioUpdate', ({ audioEnabled }: any) => {
        console.log('🎤 Self audio update:', audioEnabled);
        setIsAudioEnabled(audioEnabled);
      });

      client.self.on('videoUpdate', ({ videoEnabled }: any) => {
        console.log('📹 Self video update:', videoEnabled);
        setIsVideoEnabled(videoEnabled);
      });

      client.self.on('screenShareUpdate', ({ screenShareEnabled }: any) => {
        console.log('🎥 Self screen share update:', screenShareEnabled);
        
        if (!screenShareEnabled) {
          setIsScreenSharing(false);
          setScreenShareTracks(prev => {
            const next = new Map(prev);
            next.delete(client.self.id);
            return next;
          });
          onScreenShareUpdate?.(client.self.id, false);
        } else {
          setIsScreenSharing(true);
          onScreenShareUpdate?.(client.self.id, true);
        }
      });
    }

    // Meeting-level events
    if (client.on) {
      client.on('disconnected', () => {
        console.log('Disconnected from meeting');
        setIsJoined(false);
        onMeetingLeft?.();
      });

      client.on('error', (err: any) => {
        console.error('Meeting error:', err);
        setError(err.message || 'Unknown meeting error');
        onError?.(err);
      });
    }

    // Initialize participants list
    if (client.participants?.joined) {
      const initialParticipants = Array.from(client.participants.joined.values());
      console.log('📋 Initial participants:', initialParticipants.length);
      
      setParticipants(initialParticipants);
      
      // Check for existing screen shares
      initialParticipants.forEach((participant: any) => {
        if (participant.screenShareEnabled) {
          const track = 
            participant.screenShareTrack || 
            participant.screenShareTracks?.video ||
            participant.tracks?.screenShare;
            
          if (track) {
            console.log('✅ Initial screen share found for:', participant.id);
            setScreenShareTracks(prev => {
              const next = new Map(prev);
              next.set(participant.id, track);
              return next;
            });
          }
        }
      });
    }
    
    // Check self screen share status
    if (client.self?.screenShareEnabled) {
      console.log('🎥 Self is already screen sharing on init');
      const track = 
        client.self.screenShareTrack || 
        client.self.screenShareTracks?.video ||
        client.self.tracks?.screenShare;
        
      if (track) {
        console.log('✅ Self screen share track found on init');
        setScreenShareTracks(prev => {
          const next = new Map(prev);
          next.set(client.self.id, track);
          return next;
        });
      }
    }
  }, [onParticipantJoined, onParticipantLeft, onScreenShareUpdate, onMeetingLeft, onError]);

  const toggleAudio = useCallback(async () => {
    if (!meetingRef.current?.self) {
      throw new Error('Meeting not initialized');
    }

    try {
      if (isAudioEnabled) {
        await meetingRef.current.self.disableAudio();
      } else {
        await meetingRef.current.self.enableAudio();
      }
    } catch (err) {
      console.error('Toggle audio error:', err);
      throw err;
    }
  }, [isAudioEnabled]);

  const toggleVideo = useCallback(async () => {
    if (!meetingRef.current?.self) {
      throw new Error('Meeting not initialized');
    }

    try {
      if (isVideoEnabled) {
        await meetingRef.current.self.disableVideo();
      } else {
        await meetingRef.current.self.enableVideo();
      }
    } catch (err) {
      console.error('Toggle video error:', err);
      throw err;
    }
  }, [isVideoEnabled]);

  const toggleScreenShare = useCallback(async () => {
    if (!meetingRef.current?.self) {
      throw new Error('Meeting not initialized');
    }

    try {
      console.log('🎬 Toggling screen share, current state:', isScreenSharing);
      
      if (isScreenSharing) {
        await meetingRef.current.self.disableScreenShare();
      } else {
        await meetingRef.current.self.enableScreenShare();
        
        // Try to get the track with retries
        let attempts = 0;
        const maxAttempts = 5;
        
        const checkForTrack = () => {
          attempts++;
          console.log(`🔍 Checking for screen share track (attempt ${attempts})`);
          
          const self = meetingRef.current?.self;
          if (!self) return;
          
          const possibleTrack = 
            self.screenShareTrack ||
            self.screenShareTracks?.video ||
            self.localMediaHandler?.screenShareTrack;
          
          if (possibleTrack) {
            console.log('✅ Found track:', possibleTrack.id);
            
            setIsScreenSharing(true);
            setScreenShareTracks(prev => {
              const next = new Map(prev);
              next.set(self.id, possibleTrack);
              return next;
            });
          } else if (attempts < maxAttempts) {
            console.log('⏳ Track not found yet, retrying...');
            setTimeout(checkForTrack, 200);
          } else {
            console.error('❌ Could not find screen share track after', maxAttempts, 'attempts');
          }
        };
        
        setTimeout(checkForTrack, 100);
      }
    } catch (err) {
      console.error('Toggle screen share error:', err);
      throw err;
    }
  }, [isScreenSharing]);

  const leaveMeeting = useCallback(async () => {
    if (!meetingRef.current) return;

    try {
      await meetingRef.current.leave();
      setIsJoined(false);
      setIsInitialized(false);
      hasInitializedRef.current = false;
      isInitializingRef.current = false;
      meetingRef.current = null;
      setMeeting(null);
      setScreenShareTracks(new Map());
      onMeetingLeft?.();
    } catch (err) {
      console.error('Leave meeting error:', err);
    }
  }, [onMeetingLeft]);

  const getScreenShareTrack = useCallback((participantId: string): MediaStreamTrack | null => {
    return screenShareTracks.get(participantId) || null;
  }, [screenShareTracks]);

  useEffect(() => {
    if (authToken && !hasInitializedRef.current && !isInitializingRef.current) {
      initializeAndJoin();
    }
  }, [authToken, initializeAndJoin]);

  useEffect(() => {
    return () => {
      if (meetingRef.current && isJoined) {
        meetingRef.current.leave().catch(console.error);
      }
    };
  }, [isJoined]);

  return {
    meeting,
    isInitialized,
    isJoined,
    error,
    isAudioEnabled,
    isVideoEnabled,
    isScreenSharing,
    participants,
    self,
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
    leaveMeeting,
    audioLevels,
    connectionQuality,
    screenShareTracks,
    getScreenShareTrack,
  };
}